import React, { useCallback } from 'react'
import { GestureResponderEvent } from 'react-native'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { useFocusEffect } from '@react-navigation/native'
import { DateAdapter } from '@/data/protocols'
import { ClientMonitoringType } from '@/domain/models'
import { GetSigmaEvents, SigmaFilterType } from '@/domain/usecases'
import { Box, Button, DatePicker, Divider, InputField, SideFilter, Snackbar, Text } from '@/presentation/components'
import { useAuth, useMediaQueries, useTheme } from '@/presentation/hooks'
import { monitoringDateFiltersState, isLoadingMonitoringEventState, isOpenMonitoringFiltersState, invalidMonitoringDateState, SelectTypeSigmaEvent, remotyFilterState, sigmaFilterState, monitoringListHasError } from '@/presentation/pages/Events/components'

type SideFiltersProps = {
  dateAdapter: DateAdapter
}

type SowillDateField = 'dataInicial' | 'dataFinal'

export const SideFilters: React.FC<SideFiltersProps> = ({
  dateAdapter
}) => {
  const { getClient } = useAuth()
  const client = getClient()
  const theme = useTheme()
  const { isMobile } = useMediaQueries()

  const [monitoringFilterDate, setMonitoringFilterDate] = useRecoilState(monitoringDateFiltersState)
  const setRemotyFilter = useSetRecoilState(remotyFilterState)
  const setSigmaFilter = useSetRecoilState(sigmaFilterState)
  const [isOpen, setOpen] = useRecoilState(isOpenMonitoringFiltersState)
  const setNoMonitoringError = useSetRecoilState(monitoringListHasError)
  const [invalidDate, setInvalidDate] = useRecoilState(invalidMonitoringDateState)

  const [localFilterDate, setLocalFilterDate] = React.useState({
    dataInicial: '',
    dataFinal: ''
  })

  const isLoading = useRecoilValue(isLoadingMonitoringEventState)

  const isSowill = client.tipoConexao === ClientMonitoringType.SO_WILL

  const handleResetState = (): void => {
    setMonitoringFilterDate(currentState => ({
      ...currentState,
      dataInicial: '',
      dataFinal: ''
    })
    )

    setLocalFilterDate({ dataInicial: '', dataFinal: '' })
    setInvalidDate(false)

    setRemotyFilter({ remotyIdEquipamento: 0, remotyNmEquip: '' })

    setSigmaFilter({ sigmaTipo: null as unknown as SigmaFilterType, sigmaTituloChip: '' })
  }

  const handleClose = (): void => {
    setInvalidDate(false)
    setOpen(false)
  }

  useFocusEffect(
    useCallback(() => {
      handleResetState()
    }, [])
  )

  const isPeriodInvalid = (dataInicial: string, dataFinal: string): boolean => {
    if (!dataInicial || !dataFinal) return false
    return !dateAdapter.isBefore({
      startDate: dateAdapter.parse(dataInicial),
      endDate: dateAdapter.parse(dataFinal)
    })
  }

  const onSubmit = (event: GestureResponderEvent): void => {
    event.preventDefault()

    if (isSowill && isPeriodInvalid(localFilterDate.dataInicial, localFilterDate.dataFinal)) {
      setInvalidDate(true)
      return
    }

    setInvalidDate(false)
    setOpen(false)
    setNoMonitoringError(false)

    setMonitoringFilterDate(currentState => ({
      ...currentState,
      dataInicial: localFilterDate.dataInicial,
      dataFinal: localFilterDate.dataFinal
    }))
  }

  const handleChangeDate = useCallback((date: Date): void => {
    const dataInicial = dateAdapter.format(dateAdapter.daysAgo(GetSigmaEvents.RN.DEFAULT_DAYS, date).toString(), 'YYYY-MM-DD')
    const dataFinal = dateAdapter.format(date.toString(), 'YYYY-MM-DD')

    setLocalFilterDate({ dataInicial, dataFinal })
  }, [])

  const handleChangeSowillDate = useCallback((field: SowillDateField, date: Date): void => {
    const formatted = dateAdapter.format(date.toString(), 'YYYY-MM-DD')

    setLocalFilterDate(currentState => {
      const nextState = { ...currentState, [field]: formatted }
      setInvalidDate(isPeriodInvalid(nextState.dataInicial, nextState.dataFinal))
      return nextState
    })
  }, [])

  const sowillPeriodFilter = (
    <InputField>
      <Box style={{ flex: 1 }}>
        <DatePicker
          label='Data Início'
          disabled={isLoading}
          value={localFilterDate.dataInicial ? dateAdapter.parse(localFilterDate.dataInicial) : undefined}
          onChange={(date) => handleChangeSowillDate('dataInicial', date ?? dateAdapter.daysAgo(GetSigmaEvents.RN.DEFAULT_DAYS))}
          dateAdapter={dateAdapter}
        />
      </Box>
      <Divider />
      <Box style={{ flex: 1 }}>
        <DatePicker
          label='Data Fim'
          disabled={isLoading}
          value={localFilterDate.dataFinal ? dateAdapter.parse(localFilterDate.dataFinal) : undefined}
          onChange={(date) => handleChangeSowillDate('dataFinal', date ?? dateAdapter.today())}
          dateAdapter={dateAdapter}
        />
      </Box>
    </InputField>
  )

  const defaultPeriodFilter = (
    <>
      <Text style={{ paddingRight: '4px', paddingLeft: '4px', paddingBottom: '4px' }}>Referente aos 30 dias anteriores à data selecionada.</Text>
      <DatePicker
        label={localFilterDate.dataFinal ? dateAdapter.format(localFilterDate.dataFinal, 'DD-MM-YYYY') : 'Selecione uma data.'}
        disabled={isLoading}
        value={
          [ClientMonitoringType.SIGMA_CLOUD, ClientMonitoringType.SIGMA_CLOUD].includes(client.tipoConexao) &&
            monitoringFilterDate.dataFinal
            ? dateAdapter.parse(monitoringFilterDate.dataFinal)
            : undefined
        }
        onChange={(date) => handleChangeDate(date ?? dateAdapter.today())}
        dateAdapter={dateAdapter}
      />
    </>
  )

  return (
    <SideFilter.Drawer
      open={isOpen}
      onClose={handleClose}
      loading={isLoading}
      onSubmit={onSubmit}
      resetState={handleResetState}
    >
      <Box testID='monitoring-date-filter'>
        <Snackbar
          message='A Data Início deve ser anterior à Data Fim (período mínimo de 1 dia)'
          testID='notificacao-date-filter-monitoring'
          action={
            <Button
              variant='text'
              title='FECHAR'
              color={theme.colors.greyLight}
              onPress={() => setInvalidDate(false)}
              compact
            />
          }
          style={{
            backgroundColor: theme.colors.error,
            width: isMobile ? '100%' : '500px',
            display: invalidDate ? 'flex' : 'none'
          }}
        />
        <SideFilter.Title title='Período dos eventos' />
        {isSowill ? sowillPeriodFilter : defaultPeriodFilter}
      </Box>
      {([ClientMonitoringType.SIGMA_CLOUD].includes(client.tipoConexao) && (
        <Box>
          <SideFilter.Title title='Filtro de eventos' />
          <SelectTypeSigmaEvent />
        </Box>
      )) as React.ReactElement || undefined}
    </SideFilter.Drawer >
  )
}
